import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { FileText, Upload, Check, AlertCircle, X } from "lucide-react";
import { PurchaseOrderData, PurchaseOrderValidation } from "@/components/campaign/types/purchaseOrderV2";

interface DocumentsStepProps {
  purchaseOrderData: PurchaseOrderData;
  validation: PurchaseOrderValidation;
  onFileUpload: (file: File, type: 'companyRegistration' | 'vatRegistration') => void;
}

export const DocumentsStep = ({ 
  purchaseOrderData, 
  onFileUpload 
}: DocumentsStepProps) => {
  const { companyRegistration, vatRegistration } = purchaseOrderData;

  const handleCompanyRegUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileUpload(file, 'companyRegistration');
    }
  };

  const handleVatRegUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileUpload(file, 'vatRegistration');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Company Registration Document */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Company Registration Document</span>
            <Badge variant="destructive" className="ml-auto">
              Required
            </Badge>
            {companyRegistration && (
              <Badge variant="outline" className="ml-2">
                <Check className="h-3 w-3 mr-1" />
                Uploaded
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Upload your official company registration document issued by the relevant authorities.
          </p>

          <div className="border-2 border-dashed border-border rounded-lg p-6">
            {!companyRegistration ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                    <Upload className="h-6 w-6 text-muted-foreground" />
                  </div>
                </div>
                <div>
                  <p className="font-medium">Upload Company Registration</p>
                  <p className="text-sm text-muted-foreground">
                    PDF format only • Maximum 10MB
                  </p>
                </div>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleCompanyRegUpload}
                  className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                />
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-grow">
                  <p className="font-medium">{companyRegistration.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(companyRegistration.size)} • PDF Document
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <Check className="h-3 w-3 mr-1" />
                    Uploaded
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.pdf';
                      input.onchange = (e) => handleCompanyRegUpload(e as any);
                      input.click();
                    }}
                  >
                    Replace
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* VAT Registration Document */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>VAT Registration Document</span>
            <Badge variant="secondary" className="ml-auto">
              Optional
            </Badge>
            {vatRegistration && (
              <Badge variant="outline" className="ml-2">
                <Check className="h-3 w-3 mr-1" />
                Uploaded
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Upload your VAT registration certificate if your company is VAT registered.
          </p>

          <div className="border-2 border-dashed border-border rounded-lg p-6">
            {!vatRegistration ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                    <Upload className="h-6 w-6 text-muted-foreground" />
                  </div>
                </div>
                <div>
                  <p className="font-medium">Upload VAT Registration (Optional)</p>
                  <p className="text-sm text-muted-foreground">
                    PDF format only • Maximum 10MB
                  </p>
                </div>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleVatRegUpload}
                  className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                />
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-grow">
                  <p className="font-medium">{vatRegistration.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(vatRegistration.size)} • PDF Document
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    <Check className="h-3 w-3 mr-1" />
                    Uploaded
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.pdf';
                      input.onchange = (e) => handleVatRegUpload(e as any);
                      input.click();
                    }}
                  >
                    Replace
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Requirements Information */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-3">
              <h4 className="font-medium text-amber-900">Document Requirements</h4>
              <div className="text-sm text-amber-800 space-y-2">
                <div>
                  <p className="font-medium">Company Registration Document (Required):</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Official registration certificate from government authorities</li>
                    <li>Must be in PDF format and clearly readable</li>
                    <li>Should contain company name, registration number, and issue date</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium">VAT Registration Document (Optional):</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>VAT registration certificate if your company is VAT registered</li>
                    <li>Must be in PDF format and clearly readable</li>
                    <li>Should contain VAT number and registration details</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};